import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Button, Card, Title, Text } from "@tremor/react";
import { toast } from "sonner";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  createServerAlertRule,
  deleteServer,
  deleteServerAlertRule,
  fetchServer,
  fetchServerAlertRules,
  fetchServerMetrics,
  updateServer,
  updateServerAlertRule,
} from "../api/servers.js";
import { listChannels } from "../api/channels.js";
import { fetchIncidents } from "../api/incidents.js";

const WINDOWS = [
  { value: "1h", label: "1h" },
  { value: "6h", label: "6h" },
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
];

// Labels used in alert rule forms — must match ALERT_METRICS in lib/server_metrics.py.
const ALERT_METRIC_OPTIONS = {
  cpu_pct: "CPU %",
  cpu_steal_pct: "CPU steal %",
  mem_pct: "Memory %",
  swap_pct: "Swap %",
  disk_pct: "Disk %",
  disk_full_forecast: "Disk full forecast",
  load1: "Load avg (1m)",
  load5: "Load avg (5m)",
  load15: "Load avg (15m)",
  process_count: "Process count",
  net_rx_rate: "Net RX (KB/s)",
  net_tx_rate: "Net TX (KB/s)",
  disk_read_rate: "Disk read (KB/s)",
  disk_write_rate: "Disk write (KB/s)",
};

const _KB_RATE_METRICS = new Set(["net_rx_rate", "net_tx_rate", "disk_read_rate", "disk_write_rate"]);

// Wider label map for display (charts, rule list) — includes derived metrics.
const METRIC_LABELS = {
  ...ALERT_METRIC_OPTIONS,
  net_rx_rate: "Net RX (KB/s)",
  net_tx_rate: "Net TX (KB/s)",
  disk_read_rate: "Disk Read (KB/s)",
  disk_write_rate: "Disk Write (KB/s)",
};

function diskFillForecast(metrics) {
  const pts = metrics
    .filter((m) => m.disk_used_bytes != null && m.disk_total_bytes > 0)
    .map((m) => [new Date(m.time).getTime() / 1000, m.disk_used_bytes]);
  if (pts.length < 3) return null;
  const n = pts.length;
  const mt = pts.reduce((s, p) => s + p[0], 0) / n;
  const mu = pts.reduce((s, p) => s + p[1], 0) / n;
  const num = pts.reduce((s, p) => s + (p[0] - mt) * (p[1] - mu), 0);
  const den = pts.reduce((s, p) => s + (p[0] - mt) ** 2, 0);
  if (den === 0) return null;
  const slope = num / den;
  if (slope <= 0) return null;
  const [lt, lu] = pts[pts.length - 1];
  const total = metrics.find((m) => m.disk_total_bytes > 0)?.disk_total_bytes;
  if (!total || lu >= total) return null;
  const secsLeft = (total - lu) / slope;
  if (secsLeft > 90 * 86400) return null; // don't show if >90 days away
  return secsLeft;
}

function fmtForecast(secs) {
  if (secs < 3600) return `< ${Math.round(secs / 60)}m`;
  if (secs < 86400) return `< ${(secs / 3600).toFixed(1)}h`;
  return `< ${(secs / 86400).toFixed(1)}d`;
}

function computeRates(metrics) {
  return metrics.map((m, i) => {
    if (i === 0) {
      return { ...m, net_rx_rate: null, net_tx_rate: null, disk_read_rate: null, disk_write_rate: null };
    }
    const prev = metrics[i - 1];
    const dt = (new Date(m.time) - new Date(prev.time)) / 1000;
    if (dt <= 0) {
      return { ...m, net_rx_rate: null, net_tx_rate: null, disk_read_rate: null, disk_write_rate: null };
    }
    function rate(curr, p) {
      if (curr == null || p == null) return null;
      const delta = curr - p;
      return delta < 0 ? null : Math.round(delta / dt / 1024); // KB/s
    }
    return {
      ...m,
      net_rx_rate: rate(m.net_rx_bytes, prev.net_rx_bytes),
      net_tx_rate: rate(m.net_tx_bytes, prev.net_tx_bytes),
      disk_read_rate: rate(m.disk_read_bytes, prev.disk_read_bytes),
      disk_write_rate: rate(m.disk_write_bytes, prev.disk_write_bytes),
    };
  });
}

function relTime(iso) {
  if (!iso) return "never";
  const secs = (Date.now() - new Date(iso).getTime()) / 1000;
  if (secs < 90) return "just now";
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function fmtSeconds(s) {
  if (s == null) return "—";
  if (s < 120) return `${Math.round(s)}s`;
  if (s < 7200) return `${Math.round(s / 60)}m`;
  if (s < 172800) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

function fmtBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1073741824) return `${(n / 1048576).toFixed(1)} MB`;
  return `${(n / 1073741824).toFixed(2)} GB`;
}

function fmtUptime(secs) {
  if (secs == null) return "—";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function MetricChart({ title, data, dataKey, color, unit = "%" }) {
  return (
    <Card>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </p>
      {data.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-400">No data in range</p>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`g-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.2} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis
              dataKey="time"
              tickFormatter={(t) =>
                new Date(t).toLocaleString([], {
                  month: "short",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              }
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 10 }}
              width={42}
              tickFormatter={(v) => `${v}${unit}`}
            />
            <Tooltip
              formatter={(v) => [v != null ? `${v}${unit}` : "—", title]}
              labelFormatter={(t) => new Date(t).toLocaleString()}
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2}
              fill={`url(#g-${dataKey})`}
              dot={false}
              connectNulls
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function DualMetricChart({ title, data, keyA, keyB, labelA, labelB, colorA, colorB, unit = "KB/s" }) {
  const hasData = data.some((d) => d[keyA] != null || d[keyB] != null);
  return (
    <Card>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </p>
      {!hasData ? (
        <p className="py-12 text-center text-sm text-gray-400">No data in range</p>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`g-${keyA}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colorA} stopOpacity={0.2} />
                <stop offset="95%" stopColor={colorA} stopOpacity={0} />
              </linearGradient>
              <linearGradient id={`g-${keyB}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colorB} stopOpacity={0.2} />
                <stop offset="95%" stopColor={colorB} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
            <XAxis
              dataKey="time"
              tickFormatter={(t) =>
                new Date(t).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
              }
              tick={{ fontSize: 10 }}
              interval="preserveStartEnd"
            />
            <YAxis tick={{ fontSize: 10 }} width={48} tickFormatter={(v) => `${v}${unit}`} />
            <Tooltip
              formatter={(v, name) => [v != null ? `${v} ${unit}` : "—", name]}
              labelFormatter={(t) => new Date(t).toLocaleString()}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey={keyA} name={labelA} stroke={colorA} strokeWidth={2} fill={`url(#g-${keyA})`} dot={false} connectNulls />
            <Area type="monotone" dataKey={keyB} name={labelB} stroke={colorB} strokeWidth={2} fill={`url(#g-${keyB})`} dot={false} connectNulls />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

function NotesSection({ server, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(server.notes || "");

  async function save() {
    try {
      const updated = await updateServer(server.id, { notes: draft });
      onSave(updated.notes);
      toast.success("Notes saved");
    } catch {
      toast.error("Save failed");
    }
    setEditing(false);
  }

  return (
    <Card className="mt-4">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Notes</p>
        {!editing && (
          <button type="button" onClick={() => { setDraft(server.notes || ""); setEditing(true); }} className="text-xs text-teal-600 hover:underline">
            {server.notes ? "Edit" : "Add notes"}
          </button>
        )}
      </div>
      {editing ? (
        <div>
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            placeholder="Environment details, contacts, purpose…"
            className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm outline-none focus:border-teal-400"
          />
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={save} className="rounded bg-teal-600 px-3 py-1 text-xs font-medium text-white hover:bg-teal-700">Save</button>
            <button type="button" onClick={() => setEditing(false)} className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50">Cancel</button>
          </div>
        </div>
      ) : server.notes ? (
        <p className="whitespace-pre-wrap text-sm text-gray-700">{server.notes}</p>
      ) : (
        <p className="text-sm text-gray-400">No notes. Click "Add notes" to document this server.</p>
      )}
    </Card>
  );
}

function MountsSection({ extra }) {
  const mounts = extra?.mounts;
  if (!mounts || mounts.length === 0) return null;
  return (
    <Card>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
        Mount points
      </p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-gray-400">
            <th className="pb-1.5 pr-4">Path</th>
            <th className="pb-1.5 pr-4 text-right">Used</th>
            <th className="pb-1.5 pr-4 text-right">Total</th>
            <th className="pb-1.5 w-32">Usage</th>
          </tr>
        </thead>
        <tbody>
          {mounts.map((m) => {
            const pct = m.pct ?? (m.total > 0 ? Math.round(100 * m.used / m.total) : 0);
            const color = pct >= 90 ? "bg-red-500" : pct >= 75 ? "bg-amber-500" : "bg-teal-500";
            return (
              <tr key={m.mount} className="border-t border-gray-100">
                <td className="py-1.5 pr-4 font-mono text-xs">{m.mount}</td>
                <td className="py-1.5 pr-4 text-right text-gray-600">{fmtBytes(m.used)}</td>
                <td className="py-1.5 pr-4 text-right text-gray-600">{fmtBytes(m.total)}</td>
                <td className="py-1.5">
                  <div className="flex items-center gap-1.5">
                    <div className="h-1.5 flex-1 rounded bg-gray-100">
                      <div className={`h-1.5 rounded ${color}`} style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-7 text-right text-xs text-gray-500">{pct}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function DockerSection({ extra }) {
  const containers = extra?.docker;
  if (!containers || containers.length === 0) return null;
  const running = containers.filter((c) => c.running).length;
  return (
    <Card>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Docker containers
        </p>
        <span className="text-xs text-gray-400">
          {running}/{containers.length} running
        </span>
      </div>
      <ul className="space-y-1">
        {containers.map((c) => (
          <li key={c.name} className="flex items-center gap-2 text-sm">
            <span
              className={`h-2 w-2 flex-none rounded-full ${c.running ? "bg-emerald-500" : "bg-red-400"}`}
            />
            <span className="flex-1 truncate font-mono text-xs text-gray-800">{c.name}</span>
            <span className="truncate text-xs text-gray-400" title={c.status}>{c.status}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function AlertRulesSection({ serverId }) {
  const [rules, setRules] = useState([]);
  const [channels, setChannels] = useState([]);
  const [form, setForm] = useState({
    metric: "cpu_pct",
    comparison: "gt",
    threshold: "90",
    duration_seconds: "0",
    channel_id: "",
    severity: "high",
    allServers: false,
  });

  function load() {
    fetchServerAlertRules()
      .then((all) => setRules(all.filter((r) => !r.server_id || r.server_id === serverId)))
      .catch(() => {});
  }
  useEffect(load, [serverId]);
  useEffect(() => { listChannels().then(setChannels).catch(() => {}); }, []);

  async function add() {
    try {
      const isKbRate = _KB_RATE_METRICS.has(form.metric);
      const rawThreshold = isKbRate
        ? parseFloat(form.threshold) * 1024
        : parseFloat(form.threshold);
      await createServerAlertRule({
        server_id: form.allServers ? null : serverId,
        name: `${METRIC_LABELS[form.metric] || form.metric} ${form.comparison === "gt" ? ">" : "<"} ${form.threshold}${isKbRate ? " KB/s" : ""}`,
        metric: form.metric,
        comparison: form.metric === "disk_full_forecast" ? "lt" : form.comparison,
        threshold: rawThreshold,
        duration_seconds: parseInt(form.duration_seconds, 10) || 0,
        channel_id: form.channel_id || null,
        severity: form.severity,
      });
      toast.success("Alert rule added");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Failed to add rule");
    }
  }

  async function remove(id) {
    try {
      await deleteServerAlertRule(id);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Delete failed");
    }
  }

  return (
    <Card>
      <Title>Alert rules</Title>
      <Text className="mt-1">
        Open an incident when a metric crosses a threshold. Use “Disk full
        forecast” to get warned before the disk fills (threshold in seconds
        remaining).
      </Text>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-600">Metric</span>
          <select
            value={form.metric}
            onChange={(e) => setForm((f) => ({ ...f, metric: e.target.value }))}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            {Object.entries(ALERT_METRIC_OPTIONS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </label>
        {form.metric !== "disk_full_forecast" && (
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-gray-600">When</span>
            <select
              value={form.comparison}
              onChange={(e) => setForm((f) => ({ ...f, comparison: e.target.value }))}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="gt">above</option>
              <option value="lt">below</option>
            </select>
          </label>
        )}
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-600">
            {form.metric === "disk_full_forecast"
              ? "Alert when less than (seconds) — e.g. 86400 = 1 day"
              : _KB_RATE_METRICS.has(form.metric)
              ? "Threshold (KB/s)"
              : "Threshold"}
          </span>
          <input
            value={form.threshold}
            onChange={(e) => setForm((f) => ({ ...f, threshold: e.target.value }))}
            className="w-28 rounded border border-gray-300 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-600">Sustained for</span>
          <select
            value={form.duration_seconds}
            onChange={(e) => setForm((f) => ({ ...f, duration_seconds: e.target.value }))}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="0">Instant</option>
            <option value="60">1 min</option>
            <option value="300">5 min</option>
            <option value="600">10 min</option>
            <option value="900">15 min</option>
            <option value="1800">30 min</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-xs font-medium text-gray-600">Severity</span>
          <select
            value={form.severity}
            onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>
        {channels.length > 0 && (
          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-gray-600">Notify</span>
            <select
              value={form.channel_id}
              onChange={(e) => setForm((f) => ({ ...f, channel_id: e.target.value }))}
              className="rounded border border-gray-300 px-2 py-1.5 text-sm"
            >
              <option value="">Incident only</option>
              {channels.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
        )}
        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={form.allServers}
            onChange={(e) => setForm((f) => ({ ...f, allServers: e.target.checked }))}
            className="rounded"
          />
          Apply to all servers
        </label>
        <Button onClick={add}>Add rule</Button>
      </div>

      {rules.length > 0 && (
        <ul className="mt-4 space-y-1 text-sm">
          {rules.map((r) => (
            <AlertRuleRow
              key={r.id}
              rule={r}
              channels={channels}
              onDelete={() => remove(r.id)}
              onSave={async (patch) => {
                await updateServerAlertRule(r.id, patch);
                load();
              }}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

function AlertRuleRow({ rule: r, channels, onDelete, onSave }) {
  const [editing, setEditing] = useState(false);
  const isKb = _KB_RATE_METRICS.has(r.metric);
  const isForecast = r.metric === "disk_full_forecast";
  const displayThreshold = isForecast
    ? fmtSeconds(r.threshold)
    : isKb
    ? `${(r.threshold / 1024).toFixed(0)} KB/s`
    : String(r.threshold);
  const [draft, setDraft] = useState(displayThreshold);
  const ch = channels.find((c) => c.id === r.channel_id);

  async function save() {
    const raw = isForecast
      ? parseFloat(draft)
      : isKb
      ? parseFloat(draft) * 1024
      : parseFloat(draft);
    if (isNaN(raw)) { setEditing(false); return; }
    try {
      await onSave({ threshold: raw });
      toast.success("Rule updated");
    } catch {
      toast.error("Update failed");
    }
    setEditing(false);
  }

  return (
    <li className={`flex items-center justify-between rounded border px-3 py-1.5 ${r.is_active ? "border-gray-100" : "border-gray-100 opacity-50"}`}>
      <span className="flex items-center gap-1.5 flex-wrap">
        <span className="font-medium">{METRIC_LABELS[r.metric] || r.metric}</span>
        <span className="text-gray-500">
          {isForecast ? "< " : r.comparison === "gt" ? "> " : "< "}
        </span>
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            className="w-20 rounded border border-teal-400 px-1 py-0.5 text-xs outline-none"
          />
        ) : (
          <button
            type="button"
            className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs hover:bg-teal-50 hover:text-teal-700"
            title="Click to edit threshold"
            onClick={() => { setDraft(displayThreshold); setEditing(true); }}
          >
            {displayThreshold}
          </button>
        )}
        {isKb && !isForecast && <span className="text-gray-400 text-xs">KB/s</span>}
        {r.duration_seconds > 0 && <span className="text-gray-400 text-xs">for {fmtSeconds(r.duration_seconds)}</span>}
        {!r.server_id && <span className="text-gray-400 text-xs">· all servers</span>}
        {ch && <span className="text-teal-700 text-xs">→ {ch.name}</span>}
        {r.severity && r.severity !== "high" && (
          <span className={`text-[10px] font-medium px-1 py-0.5 rounded ${
            r.severity === "critical" ? "bg-red-100 text-red-700"
            : r.severity === "medium" ? "bg-amber-100 text-amber-700"
            : "bg-gray-100 text-gray-500"
          }`}>{r.severity}</span>
        )}
      </span>
      <div className="flex items-center gap-2">
        {r.is_breaching && (
          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">Firing</span>
        )}
        <button
          type="button"
          className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${r.is_active ? "bg-teal-50 text-teal-700 hover:bg-teal-100" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
          onClick={() => onSave({ is_active: !r.is_active }).catch(() => toast.error("Update failed"))}
        >
          {r.is_active ? "Active" : "Paused"}
        </button>
        <button className="text-xs text-red-600 hover:underline" onClick={onDelete}>
          Delete
        </button>
      </div>
    </li>
  );
}

export default function ServerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [server, setServer] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [window, setWindow] = useState("24h");
  const [loading, setLoading] = useState(true);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const rateMetrics = useMemo(() => computeRates(metrics), [metrics]);
  const forecastSecs = useMemo(() => diskFillForecast(metrics), [metrics]);

  useEffect(() => {
    fetchServer(id).then(setServer).catch(() => setServer(null));
  }, [id]);

  useEffect(() => {
    setLoading(true);
    fetchServerMetrics(id, window)
      .then(setMetrics)
      .catch(() => setMetrics([]))
      .finally(() => setLoading(false));
  }, [id, window]);

  async function saveName() {
    const name = nameDraft.trim();
    setEditingName(false);
    if (!name || name === server.display_name) return;
    try {
      const updated = await updateServer(id, { display_name: name });
      setServer(updated);
      toast.success("Renamed");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Rename failed");
    }
  }

  async function rename() {
    setNameDraft(server.display_name || server.hostname || "");
    setEditingName(true);
  }

  async function toggleActive() {
    try {
      const updated = await updateServer(id, { is_active: !server.is_active });
      setServer(updated);
      toast.success(updated.is_active ? "Server monitoring resumed" : "Server monitoring paused");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Update failed");
    }
  }

  async function remove() {
    if (!confirm("Delete this server and all its metrics?")) return;
    try {
      await deleteServer(id);
      toast.success("Server deleted");
      navigate("/servers");
    } catch (e) {
      toast.error(e?.response?.data?.error || "Delete failed");
    }
  }

  if (!server) return <p className="text-sm text-gray-500">Loading…</p>;

  const latest = server.latest || {};
  const isOffline = server.last_seen_at
    ? (Date.now() - new Date(server.last_seen_at).getTime()) > 2 * 60 * 1000
    : true;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            {editingName ? (
              <input
                autoFocus
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") setEditingName(false); }}
                className="rounded border border-teal-400 px-2 py-0.5 text-lg font-semibold outline-none"
              />
            ) : (
              <Title
                className="cursor-pointer hover:opacity-75"
                title="Click to rename"
                onClick={rename}
              >{server.display_name}</Title>
            )}
            {!server.is_active ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
                Paused
              </span>
            ) : isOffline ? (
              <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                Offline
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                Online
              </span>
            )}
            {server.open_incidents > 0 && (
              <Link
                to={`/incidents?server_id=${server.id}`}
                className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 hover:bg-red-200"
              >
                {server.open_incidents} alert{server.open_incidents > 1 ? "s" : ""}
              </Link>
            )}
          </div>
          <Text className="font-mono text-xs">
            {server.hostname} · {server.os || "—"}
            {server.agent_version && (
              <span className="ml-2 text-gray-400">agent v{server.agent_version}</span>
            )}
            {server.last_seen_at && (
              <span className="ml-2 text-gray-400">
                last seen {relTime(server.last_seen_at)}
              </span>
            )}
            {server.first_seen_at && (
              <span className="ml-2 text-gray-400">
                · enrolled {relTime(server.first_seen_at)}
              </span>
            )}
          </Text>
        </div>
        <div className="flex items-center gap-2">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              onClick={() => setWindow(w.value)}
              className={`rounded px-2 py-1 text-xs ${
                window === w.value
                  ? "bg-teal-600 text-white"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {w.label}
            </button>
          ))}
          <Button variant="secondary" size="xs" onClick={rename}>Rename</Button>
          <Button variant="secondary" size="xs" onClick={toggleActive}>
            {server.is_active ? "Pause" : "Resume"}
          </Button>
          <Button variant="secondary" size="xs" onClick={remove}>Delete</Button>
        </div>
      </div>

      {/* Snapshot */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="CPU" value={latest.cpu_pct != null ? `${Math.round(latest.cpu_pct)}%` : "—"} />
        <Stat label="Memory" value={`${fmtBytes(latest.mem_used_bytes)} / ${fmtBytes(latest.mem_total_bytes)}`} />
        <Stat
          label="Swap"
          value={
            latest.swap_total_bytes > 0
              ? `${fmtBytes(latest.swap_used_bytes)} / ${fmtBytes(latest.swap_total_bytes)}`
              : "No swap"
          }
        />
        <Stat label="Disk" value={`${fmtBytes(latest.disk_used_bytes)} / ${fmtBytes(latest.disk_total_bytes)}`} />
        <Stat label="Load (1m)" value={latest.load1 ?? "—"} />
        <Stat label="Uptime" value={fmtUptime(latest.uptime_seconds)} />
        {forecastSecs != null && (
          <Stat
            label="Disk fills in"
            value={<span className="text-amber-600">{fmtForecast(forecastSecs)}</span>}
          />
        )}
      </div>

      {!loading && metrics.length > 1 && (() => {
        const cpuVals = metrics.map((m) => m.cpu_pct).filter((v) => v != null);
        const memVals = metrics.map((m) => m.mem_pct).filter((v) => v != null);
        const stealVals = metrics.map((m) => m.cpu_steal_pct).filter((v) => v != null && v > 0);
        const avgCpu = cpuVals.length ? (cpuVals.reduce((a, b) => a + b, 0) / cpuVals.length).toFixed(1) : null;
        const peakCpu = cpuVals.length ? Math.max(...cpuVals).toFixed(1) : null;
        const avgMem = memVals.length ? (memVals.reduce((a, b) => a + b, 0) / memVals.length).toFixed(1) : null;
        const peakSteal = stealVals.length ? Math.max(...stealVals).toFixed(1) : null;
        if (!avgCpu) return null;
        return (
          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
            <span className="rounded border border-gray-200 bg-gray-50 px-2 py-1">avg CPU <strong className="text-gray-700">{avgCpu}%</strong></span>
            <span className="rounded border border-gray-200 bg-gray-50 px-2 py-1">peak CPU <strong className={parseFloat(peakCpu) >= 90 ? "text-red-600" : "text-gray-700"}>{peakCpu}%</strong></span>
            {avgMem && <span className="rounded border border-gray-200 bg-gray-50 px-2 py-1">avg mem <strong className="text-gray-700">{avgMem}%</strong></span>}
            {peakSteal && parseFloat(peakSteal) >= 2 && (
              <span className="rounded border border-red-200 bg-red-50 px-2 py-1 text-red-700">peak steal <strong>{peakSteal}%</strong></span>
            )}
            <span className="self-center text-gray-400">over {window} window</span>
          </div>
        );
      })()}

      {loading ? (
        <p className="text-sm text-gray-500">Loading metrics…</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <MetricChart title="CPU %" data={metrics} dataKey="cpu_pct" color="#0D9488" />
          {metrics.some((m) => m.cpu_steal_pct > 0) && (
            <MetricChart
              title="CPU steal %"
              data={metrics}
              dataKey="cpu_steal_pct"
              color="#ef4444"
            />
          )}
          {metrics.some((m) => m.swap_total_bytes > 0) ? (
            <DualMetricChart
              title="Memory pressure (RAM % / Swap %)"
              unit="%"
              data={metrics.map((m) => ({
                ...m,
                swap_pct:
                  m.swap_total_bytes > 0
                    ? Math.round((100 * m.swap_used_bytes) / m.swap_total_bytes)
                    : null,
              }))}
              keyA="mem_pct"
              keyB="swap_pct"
              labelA="RAM %"
              labelB="Swap %"
              colorA="#6366f1"
              colorB="#a78bfa"
            />
          ) : (
            <MetricChart title="Memory %" data={metrics} dataKey="mem_pct" color="#6366f1" />
          )}
          <MetricChart title="Disk %" data={metrics} dataKey="disk_pct" color="#f97316" />
          <MetricChart
            title="Load avg"
            data={metrics}
            dataKey="load1"
            color="#0ea5e9"
            unit=""
          />
          <DualMetricChart
            title="Network I/O"
            data={rateMetrics}
            keyA="net_rx_rate"
            keyB="net_tx_rate"
            labelA="RX"
            labelB="TX"
            colorA="#10b981"
            colorB="#8b5cf6"
          />
          <DualMetricChart
            title="Disk I/O"
            data={rateMetrics}
            keyA="disk_read_rate"
            keyB="disk_write_rate"
            labelA="Read"
            labelB="Write"
            colorA="#f59e0b"
            colorB="#ef4444"
          />
          {metrics.some((m) => m.process_count != null) && (
            <MetricChart
              title="Processes"
              data={metrics}
              dataKey="process_count"
              color="#a855f7"
              unit=""
            />
          )}
        </div>
      )}

      <MountsSection extra={latest.extra} />
      <DockerSection extra={latest.extra} />

      <NotesSection server={server} onSave={(notes) => setServer((s) => ({ ...s, notes }))} />
      <AlertRulesSection serverId={id} />
      <ServerIncidentsSection serverId={id} />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <Card className="py-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-lg font-semibold text-gray-900">{value}</p>
    </Card>
  );
}

const _STATUS_COLOR = {
  open: "bg-red-100 text-red-700",
  acknowledged: "bg-amber-100 text-amber-700",
  investigating: "bg-blue-100 text-blue-700",
  resolved: "bg-gray-100 text-gray-500",
};

function ServerIncidentsSection({ serverId }) {
  const [incidents, setIncidents] = useState([]);

  useEffect(() => {
    fetchIncidents({ server_id: serverId, limit: 10 })
      .then((data) => setIncidents(data.data || []))
      .catch(() => {});
  }, [serverId]);

  if (incidents.length === 0) return null;

  return (
    <Card className="mt-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Recent incidents
        </p>
        <Link
          to={`/incidents?server_id=${serverId}`}
          className="text-xs text-teal-600 hover:underline"
        >
          View all →
        </Link>
      </div>
      <ul className="space-y-1.5">
        {incidents.map((inc) => (
          <li key={inc.id} className="flex items-start gap-2 text-sm">
            <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${_STATUS_COLOR[inc.status] || "bg-gray-100 text-gray-500"}`}>
              {inc.status}
            </span>
            <div className="min-w-0">
              <p className="truncate text-xs font-medium text-gray-800">{inc.title}</p>
              <p className="text-[10px] text-gray-400">{relTime(inc.started_at)}</p>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
